// MCIP-28 — browser-side self-telemetry (docs/telemetry.md).
//
// Answers two questions the Aug 11 bug proved unanswerable: "did a real
// user's click fail?" and "which click was it?". Uncaught errors, React
// boundary crashes and failed API fetches are batched to POST /api/telemetry;
// every error event carries the last N interaction breadcrumbs.
//
// Design rules:
//  - Telemetry can NEVER hurt the app: everything is try/caught, the queue is
//    bounded, delivery failures are swallowed, and /api/telemetry's own
//    failures are never re-tracked (loop protection).
//  - No PII: the session id is a random per-tab token; breadcrumbs carry
//    control LABELS ("EU", "Refresh"), never form values.
const BASE = import.meta.env.VITE_API_BASE || '';
const APP_VERSION = import.meta.env.VITE_GIT_SHA || 'dev';

const FLUSH_MS = 10_000;
const MAX_QUEUE = 25;
const MAX_CRUMBS = 10;

const state = {
  queue: [], // pending events, deduped by kind|message (count increments)
  crumbs: [], // ring buffer of recent interactions
  flushTimer: null,
  backoffUntil: 0,
  sessionId: null,
  started: false,
};

function sessionId() {
  if (state.sessionId) return state.sessionId;
  try {
    let id = sessionStorage.getItem('mcip-session');
    if (!id) {
      id = Math.random().toString(16).slice(2, 10);
      sessionStorage.setItem('mcip-session', id);
    }
    state.sessionId = id;
  } catch {
    state.sessionId = 'no-storage';
  }
  return state.sessionId;
}

function route() {
  return `${window.location.pathname}${window.location.search ? '?…' : ''}`;
}

/** Last N interactions — the "which click failed" answer on every error. */
function crumb(label) {
  state.crumbs.push({ t: new Date().toISOString(), label, route: window.location.pathname });
  if (state.crumbs.length > MAX_CRUMBS) state.crumbs.shift();
}

function controlLabel(el) {
  const target = el.closest?.('button, a, select, [role="button"]');
  if (!target) return null;
  const text = (target.getAttribute('aria-label') || target.textContent || target.name || '')
    .trim().replace(/\s+/g, ' ').slice(0, 40);
  const tag = target.tagName.toLowerCase();
  return text ? `${tag} "${text}"` : tag;
}

/** Enqueue one event; identical (kind, message) pending events merge as count. */
export function track(kind, message, detail = null, { severity = 'error' } = {}) {
  try {
    const msg = String(message || '').slice(0, 500);
    if (!msg) return;
    const existing = state.queue.find((e) => e.kind === kind && e.message === msg);
    if (existing) { existing.count += 1; return; }
    if (state.queue.length >= MAX_QUEUE) return; // bounded — oldest wins, floods drop
    state.queue.push({
      kind,
      message: msg,
      severity,
      route: route(),
      ts: new Date().toISOString(),
      count: 1,
      detail: { ...(detail || {}), breadcrumbs: [...state.crumbs] },
    });
    scheduleFlush();
  } catch { /* never throw from telemetry */ }
}

/** api.js calls this on any failed fetch. Loop protection lives HERE. */
export function trackApiError(path, status, message) {
  const clean = String(path || '').split('?')[0];
  if (clean.startsWith('/api/telemetry')) return; // never track the tracker
  track('api_fetch_error', `${clean} → ${status ?? 'network'}: ${message}`, { path: clean, status: status ?? null });
}

function payload() {
  return JSON.stringify({
    session_id: sessionId(),
    app_version: APP_VERSION,
    events: state.queue.splice(0, MAX_QUEUE),
  });
}

function flush() {
  if (!state.queue.length || Date.now() < state.backoffUntil) return;
  const body = payload();
  fetch(`${BASE}/api/telemetry`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true, // survives navigation for small bodies
  }).then((res) => {
    if (res.status === 429) state.backoffUntil = Date.now() + 120_000; // server said slow down
  }).catch(() => {
    state.backoffUntil = Date.now() + 60_000; // API unreachable — don't hammer it
  });
}

function scheduleFlush() {
  if (state.flushTimer) return;
  state.flushTimer = setTimeout(() => {
    state.flushTimer = null;
    flush();
  }, FLUSH_MS);
}

/** Wire the global hooks once, from main.jsx. Idempotent. */
export function initTelemetry() {
  if (state.started) return;
  state.started = true;
  try {
    // Breadcrumbs: capture phase sees the click even when the handler throws.
    document.addEventListener('click', (e) => {
      try {
        const label = controlLabel(e.target);
        if (label) crumb(`click ${label}`);
      } catch { /* breadcrumbs are best-effort */ }
    }, { capture: true, passive: true });

    window.addEventListener('error', (e) => {
      track('js_error', e.message || 'unknown error', {
        source: e.filename ? `${e.filename.split('/').pop()}:${e.lineno}` : null,
        stack: e.error?.stack?.split('\n').slice(0, 5).join('\n') || null,
      });
    });

    window.addEventListener('unhandledrejection', (e) => {
      const reason = e.reason;
      track('unhandled_rejection', reason?.message || String(reason ?? 'unknown rejection'), {
        stack: reason?.stack?.split('\n').slice(0, 5).join('\n') || null,
      });
    });

    // Drain on tab close — sendBeacon delivers where fetch would be cancelled.
    window.addEventListener('pagehide', () => {
      if (!state.queue.length) return;
      try {
        navigator.sendBeacon(`${BASE}/api/telemetry`, new Blob([payload()], { type: 'application/json' }));
      } catch { /* nothing left to do on the way out */ }
    });
  } catch { /* a telemetry init failure must never block the app */ }
}
